import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  MessageSquare,
  Clock,
  User,
  ExternalLink,
  FileText,
  AlertCircle,
  CheckCircle2,
  Wrench,
  Eye,
  EyeOff,
  RefreshCw,
} from "lucide-react";
import { FormattedDate } from "@/components/ui/formatted-date";

interface TicketNote {
  id: string;
  ticketId: string;
  text: string;
  detailDescriptionFlag?: boolean;
  internalAnalysisFlag?: boolean;
  resolutionFlag?: boolean;
  internalFlag?: boolean;
  externalFlag?: boolean;
  createdBy?: string;
  dateCreated?: string;
}

interface TicketData {
  id: string;
  summary: string;
  status?: string;
  priority?: string;
  type?: string;
  boardName?: string;
  assignedResourceName?: string;
  dateEntered?: string;
  lastUpdated?: string;
  closedDate?: string;
  actualHours?: number;
  budgetHours?: number;
}

interface TicketDiscussionDialogProps {
  ticket: TicketData | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ticketUrl?: string | null;
  /** When true, internal notes are hidden by default (e.g. during customer-facing BR presentations) */
  hideInternalByDefault?: boolean;
}

// Get note type badge
const getNoteTypeBadge = (note: TicketNote) => {
  if (note.resolutionFlag) {
    return (
      <Badge variant="default" className="bg-green-600 text-xs">
        <CheckCircle2 className="h-3 w-3 mr-1" />
        Resolution
      </Badge>
    );
  }
  if (note.internalAnalysisFlag) {
    return (
      <Badge variant="secondary" className="text-xs">
        <Wrench className="h-3 w-3 mr-1" />
        Internal Analysis
      </Badge>
    );
  }
  if (note.detailDescriptionFlag) {
    return (
      <Badge variant="outline" className="text-xs">
        <FileText className="h-3 w-3 mr-1" />
        Description
      </Badge>
    );
  }
  return null;
};

// Get visibility badge
const getVisibilityBadge = (note: TicketNote) => {
  if (note.internalFlag && !note.externalFlag) {
    return (
      <Badge variant="outline" className="text-xs text-orange-600 border-orange-300">
        <EyeOff className="h-3 w-3 mr-1" />
        Internal
      </Badge>
    );
  }
  if (note.externalFlag) {
    return (
      <Badge variant="outline" className="text-xs text-blue-600 border-blue-300">
        <Eye className="h-3 w-3 mr-1" />
        Customer Visible
      </Badge>
    );
  }
  return null;
};

export function TicketDiscussionDialog({
  ticket,
  open,
  onOpenChange,
  ticketUrl,
  hideInternalByDefault = false,
}: TicketDiscussionDialogProps) {
  const [showInternalNotes, setShowInternalNotes] = useState(!hideInternalByDefault);

  // Detect active PSA provider for notes endpoint
  const { data: psaIntegrations = [] } = useQuery<any[]>({
    queryKey: ["/api/integrations/status"],
    enabled: open,
  });

  const activePsaProvider = psaIntegrations.find(
    (i: any) => i.provider === "connectwise" && i.status === "active"
  ) ? "connectwise" : psaIntegrations.find(
    (i: any) => i.provider === "halopsa" && i.status === "active"
  ) ? "halopsa" : psaIntegrations.find(
    (i: any) => i.provider === "connectwise_asio" && i.status === "active"
  ) ? "connectwise_asio" : null;

  // Fetch ticket notes
  const {
    data: notes = [],
    isLoading,
    error,
    refetch,
  } = useQuery<TicketNote[]>({
    queryKey: ["/api/integrations/tickets/notes", activePsaProvider, ticket?.id],
    queryFn: async () => {
      if (!ticket?.id) return [];

      let url: string;
      if (activePsaProvider === "connectwise_asio") {
        url = `/api/integrations/connectwise-asio/tickets/${ticket.id}/notes`;
      } else if (activePsaProvider === "halopsa") {
        url = `/api/integrations/halopsa/tickets/${ticket.id}/notes`;
      } else {
        url = `/api/integrations/connectwise/tickets/${ticket.id}/notes`;
      }

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error("Failed to fetch ticket notes");
      }
      const rawNotes = await response.json();

      // Normalize Asio notes to match the expected TicketNote shape
      if (activePsaProvider === "connectwise_asio") {
        return rawNotes.map((n: any) => ({
          id: n.id,
          ticketId: n.ticketId,
          text: n.detail || '',
          internalFlag: n.visibility === 2,
          externalFlag: n.visibility === 1,
          createdBy: n.createdBy,
          dateCreated: n.createdAt,
        }));
      }

      return rawNotes;
    },
    enabled: open && !!ticket?.id && !!activePsaProvider,
    staleTime: 30 * 1000, // 30 seconds
  });

  // Filter notes based on visibility preference
  const filteredNotes = showInternalNotes
    ? notes
    : notes.filter((note) => !note.internalFlag || note.externalFlag);

  // Sort notes by date (newest first)
  const sortedNotes = [...filteredNotes].sort((a, b) => {
    const dateA = a.dateCreated ? new Date(a.dateCreated).getTime() : 0;
    const dateB = b.dateCreated ? new Date(b.dateCreated).getTime() : 0;
    return dateB - dateA;
  });

  // Get summary of discussion
  const getDiscussionSummary = () => {
    const totalNotes = notes.length;
    const resolutionNotes = notes.filter((n) => n.resolutionFlag).length;
    const internalNotes = notes.filter((n) => n.internalFlag && !n.externalFlag).length;
    const customerVisibleNotes = notes.filter((n) => n.externalFlag).length;

    return { totalNotes, resolutionNotes, internalNotes, customerVisibleNotes };
  };

  const summary = getDiscussionSummary();

  if (!ticket) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <div className="flex items-start justify-between gap-4 pr-6">
            <div className="flex-1 min-w-0">
              <DialogTitle className="flex items-center gap-2 text-lg">
                <MessageSquare className="h-5 w-5 flex-shrink-0" />
                <span className="truncate">Ticket #{ticket.id}</span>
              </DialogTitle>
              <DialogDescription className="mt-1 line-clamp-2">
                {ticket.summary}
              </DialogDescription>
            </div>
            {ticketUrl && (
              <Button variant="outline" size="sm" asChild className="flex-shrink-0">
                <a href={ticketUrl} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="h-4 w-4 mr-1" />
                  Open in PSA
                </a>
              </Button>
            )}
          </div>
        </DialogHeader>

        {/* Ticket Info Summary */}
        <div className="flex flex-wrap gap-2 py-2 border-b">
          {ticket.status && (
            <Badge variant="outline" className="text-xs">
              Status: {ticket.status}
            </Badge>
          )}
          {ticket.priority && (
            <Badge variant="outline" className="text-xs">
              Priority: {ticket.priority}
            </Badge>
          )}
          {ticket.type && (
            <Badge variant="outline" className="text-xs">
              Type: {ticket.type}
            </Badge>
          )}
          {ticket.assignedResourceName && (
            <Badge variant="outline" className="text-xs">
              <User className="h-3 w-3 mr-1" />
              {ticket.assignedResourceName}
            </Badge>
          )}
        </div>

        {/* Discussion Stats */}
        <div className="flex items-center justify-between py-2">
          <div className="flex gap-4 text-sm text-muted-foreground">
            <span>{summary.totalNotes} notes</span>
            {summary.resolutionNotes > 0 && (
              <span className="text-green-600">{summary.resolutionNotes} resolution</span>
            )}
            {summary.customerVisibleNotes > 0 && (
              <span className="text-blue-600">{summary.customerVisibleNotes} customer visible</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowInternalNotes(!showInternalNotes)}
              className="text-xs"
            >
              {showInternalNotes ? (
                <>
                  <EyeOff className="h-3 w-3 mr-1" />
                  Hide Internal
                </>
              ) : (
                <>
                  <Eye className="h-3 w-3 mr-1" />
                  Show All
                </>
              )}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => refetch()}>
              <RefreshCw className="h-3 w-3" />
            </Button>
          </div>
        </div>

        {/* Notes List */}
        <ScrollArea className="flex-1 min-h-0 pr-4">
          {isLoading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="space-y-2">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-20 w-full" />
                </div>
              ))}
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <AlertCircle className="h-10 w-10 mx-auto text-red-500 mb-2" />
              <p className="text-sm text-muted-foreground">Failed to load ticket notes</p>
              <Button variant="outline" size="sm" onClick={() => refetch()} className="mt-2">
                Try Again
              </Button>
            </div>
          ) : sortedNotes.length === 0 ? (
            <div className="text-center py-8">
              <MessageSquare className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">
                {notes.length === 0
                  ? "No notes or discussions on this ticket yet"
                  : "No visible notes match your filter"}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {sortedNotes.map((note) => (
                <div
                  key={note.id}
                  className={`p-3 rounded-lg border ${
                    note.resolutionFlag
                      ? "bg-green-50 border-green-200 dark:bg-green-950/30 dark:border-green-800"
                      : note.internalFlag && !note.externalFlag
                      ? "bg-orange-50 border-orange-200 dark:bg-orange-950/30 dark:border-orange-800"
                      : "bg-muted/30"
                  }`}
                >
                  {/* Note Header */}
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      {getNoteTypeBadge(note)}
                      {getVisibilityBadge(note)}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      {note.createdBy && (
                        <span className="flex items-center gap-1">
                          <User className="h-3 w-3" />
                          {note.createdBy}
                        </span>
                      )}
                      {note.dateCreated && (
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          <FormattedDate date={note.dateCreated} format="full" />
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Note Content */}
                  <div className="text-sm whitespace-pre-wrap break-words">
                    {note.text}
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
